import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Trash2,
  Zap,
  Edit2,
  Search,
  ChevronRight,
  ArrowUpDown,
  ListOrdered,
  List,
  Hash,
  ToggleLeft,
  AlignLeft,
  Info,
  Sliders,
  MapPin,
  HelpCircle,
  X,
} from 'lucide-react';
import { htmlToPlainText } from './utils';

const sectionMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
};

// Canonical type buckets. Order here = display order in the panel.
const TYPE_GROUPS = [
  { key: 'ordinal',     label: 'Ordinal',        short: 'ORD',  icon: ListOrdered },
  { key: 'nominal',     label: 'Nominal',        short: 'NOM',  icon: List },
  { key: 'numeric',     label: 'Numerical',      short: 'NRS',  icon: Hash },
  { key: 'vas',         label: 'Visual',         short: 'VAS',  icon: Sliders },
  { key: 'boolean',     label: 'Yes / No',       short: 'Y/N',  icon: ToggleLeft },
  { key: 'text',        label: 'Text',           short: 'TEXT', icon: AlignLeft },
  { key: 'instruction', label: 'Instruction',    short: 'INST', icon: Info },
  { key: 'body_map',    label: 'Body Map',       short: 'BODY', icon: MapPin },
  { key: 'other',       label: 'Other',          short: '—',    icon: HelpCircle },
  { key: 'irt',         label: 'IRT Calibrated', short: 'IRT',  icon: Zap },
];

const TYPE_GROUPS_BY_KEY = TYPE_GROUPS.reduce((acc, g) => {
  acc[g.key] = g;
  return acc;
}, {});

const normalizeType = (item) => {
  const t = (item.type || item.response_type || item.question_type || '').toLowerCase();
  if (t === 'likert') return 'ordinal';
  if (t === 'rating' || t === 'nrs') return 'numeric';
  if (t === 'yes_no') return 'boolean';
  if (TYPE_GROUPS_BY_KEY[t]) return t;
  return 'other';
};

const isIrtItem = (item) => {
  const irt = item.irt_parameters || {};
  const hasSlope = irt.slope !== undefined && irt.slope !== null;
  const hasDiscrimination = irt.discrimination !== undefined && irt.discrimination !== null;
  const hasThresholds =
    irt.thresholds !== undefined &&
    irt.thresholds !== null &&
    Array.isArray(irt.thresholds) &&
    irt.thresholds.length > 0;
  return hasSlope || hasDiscrimination || hasThresholds || item.category === 'C';
};

const SORT_OPTIONS = [
  { key: 'code',   label: 'Code A→Z' },
  { key: 'recent', label: 'Recently added' },
  { key: 'screen', label: 'On screen first' },
];

const sortItems = (list, sortBy, onScreenIds) => {
  const out = [...list];
  if (sortBy === 'code') {
    out.sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
  } else if (sortBy === 'recent') {
    out.sort((a, b) => (b.id || 0) - (a.id || 0));
  } else if (sortBy === 'screen') {
    out.sort((a, b) => {
      const aOn = onScreenIds.has(a.id) ? 0 : 1;
      const bOn = onScreenIds.has(b.id) ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
    });
  }
  return out;
};

const AssessmentItemsPanel = ({
  items,
  screens,
  currentScreenIndex,
  selectedElement,
  setSelectedElement,
  setShowItemBank,
  setDraggingFromSidebar,
  setDragPreviewPosition,
  addElementToScreen,
  removeAssessmentItem,
  removeAllItems,
  onEditItem,
}) => {
  const currentScreen = Array.isArray(screens) ? screens[currentScreenIndex] : undefined;

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('code');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onScreenIdSet = useMemo(
    () =>
      new Set(
        (currentScreen?.elements || [])
          .filter((el) => el.type === 'question' || el.type === 'instruction')
          .map((el) => el.questionId)
      ),
    [currentScreen]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((item) => {
      const code = (item.code || '').toLowerCase();
      const text = htmlToPlainText(item.text).toLowerCase();
      return code.includes(q) || text.includes(q);
    });
  }, [items, search]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const item of filtered) {
      const key = isIrtItem(item) ? 'irt' : normalizeType(item);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(item);
    }
    return TYPE_GROUPS
      .map((g) => ({ ...g, items: sortItems(buckets.get(g.key) || [], sortBy, onScreenIdSet) }))
      .filter((g) => g.items.length > 0);
  }, [filtered, sortBy, onScreenIdSet]);

  const totalCount = items?.length || 0;
  const visibleCount = filtered.length;

  const renderItemCard = (item, group) => {
    const existingElement = (currentScreen?.elements || []).find(
      (el) =>
        (el.type === 'question' || el.type === 'instruction') &&
        el.questionId === item.id
    );
    const isSelected = existingElement && existingElement.id === selectedElement;
    const isIrt = group.key === 'irt';

    return (
      <div key={item.id} className="relative group/item">
        <button
          type="button"
          draggable="true"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/json', JSON.stringify({ type: 'question', item }));
            setDraggingFromSidebar({ type: 'question', item });
          }}
          onDragEnd={() => {
            setDraggingFromSidebar(null);
            setDragPreviewPosition(null);
          }}
          onClick={() => {
            if (existingElement) {
              if (setSelectedElement) setSelectedElement(existingElement.id);
            } else {
              addElementToScreen('question', item);
            }
          }}
          className={`w-full flex flex-col items-start text-left px-3 py-2 rounded-lg border transition-colors cursor-grab active:cursor-grabbing select-none ${
            isSelected
              ? isIrt
                ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600'
                : 'bg-blue-100 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600'
              : existingElement
                ? isIrt
                  ? 'bg-white dark:bg-gray-800 border-purple-200 dark:border-purple-700 hover:border-purple-400'
                  : 'bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-700 hover:border-blue-400'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          style={
            existingElement
              ? { borderLeftWidth: '3px', borderLeftColor: isIrt ? '#a855f7' : '#3b82f6' }
              : undefined
          }
        >
          <div className="flex items-center justify-between w-full">
            <span
              className={`text-xs font-semibold truncate pr-2 ${
                isIrt
                  ? 'text-purple-700 dark:text-purple-300'
                  : 'text-gray-800 dark:text-gray-200'
              }`}
            >
              {item.code || `Q${item.rank !== undefined ? item.rank + 1 : item.id}`}
            </span>
            <span className="flex items-center gap-1.5 flex-shrink-0 transition-opacity group-hover/item:opacity-0">
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {group.short}
              </span>
              {existingElement && (
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    isIrt ? 'bg-purple-500' : 'bg-blue-500'
                  }`}
                  title="On current screen"
                />
              )}
            </span>
          </div>
          <span className="text-[11px] text-gray-600 dark:text-gray-400 text-left line-clamp-2 mt-0.5 pointer-events-none whitespace-pre-line">
            {htmlToPlainText(item.text)}
          </span>
          {isIrt && item.irt_parameters && (
            <div className="mt-1 pt-1 border-t border-purple-100 dark:border-purple-800/50 text-[9px] text-purple-600 dark:text-purple-400 w-full">
              {(item.irt_parameters.slope || item.irt_parameters.discrimination) && (
                <span className="mr-2">
                  a={item.irt_parameters.slope || item.irt_parameters.discrimination}
                </span>
              )}
              {item.irt_parameters.thresholds && Array.isArray(item.irt_parameters.thresholds) && (
                <span>b=[{item.irt_parameters.thresholds.filter((t) => t !== -99).join(', ')}]</span>
              )}
            </div>
          )}
        </button>

        <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
          {onEditItem && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditItem(item);
              }}
              className="p-1 rounded text-gray-400 hover:text-primary-600 hover:bg-white dark:hover:bg-gray-700"
              title="Edit item"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeAssessmentItem(item);
            }}
            className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-white dark:hover:bg-gray-700"
            title="Remove from assessment"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  const renderGroup = (group) => {
    const expanded = expandedGroups.has(group.key);
    const onScreenCount = group.items.filter((i) => onScreenIdSet.has(i.id)).length;
    const Icon = group.icon;
    const isIrt = group.key === 'irt';

    return (
      <div
        key={group.key}
        className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800"
      >
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${
            expanded
              ? isIrt
                ? 'bg-purple-50 dark:bg-purple-900/20'
                : 'bg-primary-50/60 dark:bg-primary-900/10'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
          aria-expanded={expanded}
        >
          <div className="flex items-center space-x-2.5 min-w-0">
            <div
              className={`p-1.5 rounded-lg flex-shrink-0 ${
                isIrt
                  ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300'
                  : 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">
                {group.label}
              </div>
              {onScreenCount > 0 && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                  {onScreenCount} on screen
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                isIrt
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              {group.items.length}
            </span>
            <motion.span
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="inline-flex"
            >
              <ChevronRight
                className={`h-4 w-4 ${
                  expanded ? (isIrt ? 'text-purple-600' : 'text-primary-600') : 'text-gray-400'
                }`}
              />
            </motion.span>
          </div>
        </button>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div {...sectionMotion} className="overflow-hidden">
              <div className="p-2 space-y-1.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
                {group.items.map((item) => renderItemCard(item, group))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
            Assessment Items
          </h3>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
            {totalCount}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {totalCount > 0 && removeAllItems && (
            <button
              type="button"
              onClick={removeAllItems}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Remove all items"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowItemBank(true)}
            className="p-1 text-primary-600 hover:text-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            title="Import Items from Library"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {totalCount > 0 && (
        <div className="mb-3 space-y-2 px-1">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items..."
              className="w-full text-xs pl-8 pr-7 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Sort + count */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              {search
                ? `${visibleCount} of ${totalCount}`
                : `${totalCount} item${totalCount === 1 ? '' : 's'}`}
            </span>
            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
              <ArrowUpDown className="w-3 h-3" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-[10px] bg-transparent border-none focus:outline-none focus:ring-0 text-gray-600 dark:text-gray-300 cursor-pointer pr-1"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="space-y-2">
        {totalCount === 0 ? (
          <div className="text-xs text-gray-500 text-center py-6 px-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="mb-2">No items in this assessment</p>
            <button
              type="button"
              onClick={() => setShowItemBank(true)}
              className="text-primary-600 hover:text-primary-800 font-medium"
            >
              + Add from Item Library
            </button>
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-xs text-gray-500 text-center py-6 px-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            No items match &ldquo;{search}&rdquo;
          </div>
        ) : (
          grouped.map(renderGroup)
        )}
      </div>
    </div>
  );
};

export default AssessmentItemsPanel;
